import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Brain, 
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
  Download,
  Target,
  FileSpreadsheet,
  Users,
  IndianRupee,
  Package,
  Store,
  Activity,
  CheckCircle,
  XCircle,
  AlertCircle,
  Briefcase,
  Building2,
  ShoppingCart,
  Truck,
  HeartPulse,
  Shield
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ExecutiveBrainDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [activeRole, setActiveRole] = useState('CEO');
  const { languageConfig } = useLanguage();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/agent-dashboards/executive-brain`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load ExecutiveBrain dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async (dataType, label) => {
    setExporting(dataType);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/agent-dashboards/executive-brain/export`,
        {
          params: { data_type: dataType },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `executive_brain_${dataType}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      toast.success(`${label} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${label}`);
    } finally {
      setExporting(null);
    }
  };

  const formatCurrency = (value) => {
    if (typeof value === 'string') return value;
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)} K`;
    return `₹${value?.toFixed(0) || 0}`;
  };

  const formatNumber = (value) => {
    if (typeof value === 'string') return value;
    return value?.toLocaleString() || '0';
  };

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'HEALTHY': return 'bg-green-100 text-green-800 border-green-300';
      case 'NEEDS_ATTENTION': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-slate-100 text-slate-800 border-slate-300';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'CEO': return <Briefcase className="h-4 w-4" />;
      case 'CFO': return <IndianRupee className="h-4 w-4" />;
      case 'Merchandising': return <ShoppingCart className="h-4 w-4" />;
      case 'Supply_Chain': return <Truck className="h-4 w-4" />;
      case 'Store_Ops': return <Store className="h-4 w-4" />;
      case 'CRM': return <Users className="h-4 w-4" />;
      case 'Audit': return <Shield className="h-4 w-4" />;
      default: return <Building2 className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="executive-brain-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!data?.hasData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4" data-testid="executive-brain-no-data">
        <Brain className="h-16 w-16 text-slate-400" />
        <h2 className="text-xl font-semibold text-slate-700">No Analysis Available</h2>
        <p className="text-slate-500">{data?.message || 'Please run the ReasonExecutiveBrain agent first.'}</p>
        <Button onClick={() => window.location.href = '/agents'}>Go to Agents</Button>
      </div>
    );
  }

  const snapshot = data?.executiveSnapshot || {};
  const big5Drivers = data?.big5Drivers || [];
  const hotspots = data?.exceptionHotspots || {};
  const roleDashboards = data?.roleDashboards || {};
  const actionPlan = data?.actionPlan || {};
  const diagnostics = data?.diagnostics || {};
  const recommendations = data?.recommendations || [];
  const keyInsights = data?.keyInsights || [];

  const COLORS = ['#6366f1', '#22c55e', '#f97316', '#ef4444', '#8b5cf6', '#ec4899'];

  const roles = ['CEO', 'CFO', 'Merchandising', 'Supply_Chain', 'Store_Ops', 'CRM', 'Audit'];

  return (
    <div className="space-y-6" data-testid="executive-brain-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Brain className="h-8 w-8 text-indigo-600" />
            <TranslatedText text="Executive Brain Dashboard" cacheKey="execbrain:title" />
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="META-ORCHESTRATION: Unified Executive Action Plan (Priority 0)" cacheKey="execbrain:subtitle" />
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('all_data', 'Full Analysis')}>
            <Download className="h-4 w-4 mr-2" />Export All
          </Button>
        </div>
      </div>

      {/* Health Status Banner */}
      <Card className={`border-2 ${getHealthStatusColor(snapshot.healthStatus)}`}>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <HeartPulse className="h-8 w-8" />
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: languageConfig?.font }}>
                  <TranslatedText text="Business Health" cacheKey="execbrain:health_label" />: {snapshot.healthStatus || 'UNKNOWN'}
                </h2>
                <p className="text-sm opacity-80" style={{ fontFamily: languageConfig?.font }}>
                  <TranslatedText text="Last analyzed" cacheKey="execbrain:last_analyzed" />: {data?.executedAt ? new Date(data.executedAt).toLocaleString() : 'N/A'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">{formatCurrency(snapshot.totalRevenue)}</p>
              <p className="text-sm opacity-80" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Total Revenue" cacheKey="execbrain:total_revenue_label" /></p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Executive Summary */}
      <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 border-indigo-200">
        <CardHeader><CardTitle><TranslatedText text="Executive Summary" cacheKey="execbrain:execsummary" /></CardTitle></CardHeader>
        <CardContent>
          <p className="text-slate-700 dark:text-slate-300 whitespace-pre-line" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text={data?.summary} cacheKey="execbrain:summary" />
          </p>
        </CardContent>
      </Card>

      {/* Executive Snapshot KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <p className="text-xs text-blue-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Total Revenue" cacheKey="execbrain:total_revenue" /></p>
            <p className="text-xl font-bold text-blue-900">{formatCurrency(snapshot.totalRevenue)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <p className="text-xs text-green-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Total Bills" cacheKey="execbrain:total_bills" /></p>
            <p className="text-xl font-bold text-green-900">{formatNumber(snapshot.totalBills)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4">
            <p className="text-xs text-purple-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Average Selling Price" cacheKey="execbrain:asp" /></p>
            <p className="text-xl font-bold text-purple-900">{formatCurrency(snapshot.asp)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-4">
            <p className="text-xs text-orange-700" style={{ fontFamily: languageConfig?.font }}><TranslatedText text="Incentive Rate" cacheKey="execbrain:incentive_rate" /></p>
            <p className="text-xl font-bold text-orange-900">{snapshot.incentiveRate || 'N/A'}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-4">
            <p className="text-xs text-red-700">Return Rate</p>
            <p className="text-xl font-bold text-red-900">{snapshot.returnRate || 'N/A'}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="pt-4">
            <p className="text-xs text-yellow-700">Stockout Risk</p>
            <p className="text-xl font-bold text-yellow-900">{formatNumber(snapshot.stockoutRiskCount)} SKUs</p>
          </CardContent>
        </Card>
      </div>

      {/* Big 5 Drivers */}
      <Card className="border-indigo-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-indigo-800">
            <Activity className="h-5 w-5" />
            Big 5 Business Drivers
          </CardTitle>
          <CardDescription>Evidence-based factors impacting business performance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {big5Drivers.slice(0, 5).map((driver, idx) => (
              <div key={idx} className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-bold">
                    {idx + 1}
                  </span>
                  <h4 className="font-semibold text-indigo-800 text-sm">{driver.title || driver.name || `Driver ${idx + 1}`}</h4>
                </div>
                <p className="text-xs text-slate-600">{driver.evidence || driver.description || driver}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top 5 Recommendations with Download All */}
      <Card className="border-indigo-200 bg-indigo-50/30">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-indigo-800">
              <Target className="h-5 w-5" />
              Top 5 Executive Recommendations
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExport('all_data', 'All Recommendations')} disabled={exporting === 'all_data'}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Download All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recommendations.slice(0, 5).map((rec, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-white dark:bg-slate-800 rounded-lg border">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                  idx === 0 ? 'bg-indigo-600' : idx === 1 ? 'bg-blue-500' : 'bg-slate-500'
                }`}>{idx + 1}</span>
                <div className="flex-1">
                  <p className="font-medium">{rec.action_title || rec.description || rec.action || rec.text || rec}</p>
                  {rec.owner_role && <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded mr-2">{rec.owner_role}</span>}
                  {rec.expected_impact && <p className="text-sm text-indigo-600 mt-1">Impact: {rec.expected_impact}</p>}
                  {rec.confidence && <span className={`text-xs px-2 py-0.5 rounded ${rec.confidence === 'HIGH' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{rec.confidence}</span>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Exception Hotspots */}
      <Card className="border-red-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Exception Hotspots
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExport('hotspots', 'Hotspots')} disabled={exporting === 'hotspots'}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Export All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Stores */}
            <div className="p-3 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Store className="h-4 w-4 text-red-600" />
                <h4 className="font-semibold text-red-700">Store Issues</h4>
              </div>
              <p className="text-2xl font-bold text-red-800">{(hotspots.stores || []).length}</p>
              <ul className="text-xs text-slate-600 mt-2 space-y-1 max-h-20 overflow-y-auto">
                {(hotspots.stores || []).slice(0, 3).map((store, idx) => (
                  <li key={idx}>{store.storeName || store.storeId || store}</li>
                ))}
              </ul>
            </div>
            
            {/* SKUs */}
            <div className="p-3 bg-orange-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Package className="h-4 w-4 text-orange-600" />
                <h4 className="font-semibold text-orange-700">SKU Issues</h4>
              </div>
              <p className="text-2xl font-bold text-orange-800">{(hotspots.skus || []).length}</p>
              <ul className="text-xs text-slate-600 mt-2 space-y-1 max-h-20 overflow-y-auto">
                {(hotspots.skus || []).slice(0, 3).map((sku, idx) => (
                  <li key={idx}>{sku.skuId || sku.name || sku}</li>
                ))}
              </ul>
            </div>
            
            {/* Promos */}
            <div className="p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="h-4 w-4 text-yellow-600" />
                <h4 className="font-semibold text-yellow-700">Promo Issues</h4>
              </div>
              <p className="text-2xl font-bold text-yellow-800">{(hotspots.promos || []).length}</p>
            </div>
            
            {/* Vendors */}
            <div className="p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Truck className="h-4 w-4 text-purple-600" />
                <h4 className="font-semibold text-purple-700">Vendor Issues</h4>
              </div>
              <p className="text-2xl font-bold text-purple-800">{(hotspots.vendors || []).length}</p>
            </div>
            
            {/* Governance */}
            <div className="p-3 bg-slate-100 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-slate-600" />
                <h4 className="font-semibold text-slate-700">Governance</h4>
              </div>
              <p className="text-2xl font-bold text-slate-800">{(hotspots.governance || []).length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Role-Based Dashboards */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Role-Based Executive Dashboards
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Role Tabs */}
          <div className="flex flex-wrap gap-2 mb-4 border-b pb-4">
            {roles.map((role) => (
              <Button
                key={role}
                variant={activeRole === role ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveRole(role)}
                className="flex items-center gap-1"
              >
                {getRoleIcon(role)}
                {role.replace('_', ' ')}
              </Button>
            ))}
          </div>
          
          {/* Active Role Dashboard */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
              {getRoleIcon(activeRole)}
              {activeRole.replace('_', ' ')} Dashboard
            </h4>
            {roleDashboards[activeRole] ? (
              <div className="space-y-2">
                {Object.entries(roleDashboards[activeRole]).map(([key, value], idx) => (
                  <div key={idx} className="flex justify-between items-center p-2 bg-white rounded border">
                    <span className="font-medium text-sm">{key.replace(/_/g, ' ')}</span>
                    <span className="text-sm text-slate-600">
                      {typeof value === 'object' ? JSON.stringify(value).slice(0, 50) : value}
                    </span>
                  </div>
                ))}
                {Object.keys(roleDashboards[activeRole]).length === 0 && (
                  <p className="text-slate-500">No specific data for this role</p>
                )}
              </div>
            ) : (
              <p className="text-slate-500">No dashboard data available for {activeRole}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Prioritized Action Plan */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Prioritized Action Plan</CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExport('action_plan', 'Action Plan')} disabled={exporting === 'action_plan'}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* P0 - Immediate */}
            <div className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-red-800 mb-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                P0 - Today/24h
              </h4>
              <ul className="space-y-2 text-sm">
                {(actionPlan.P0_Immediate || []).slice(0, 5).map((action, idx) => (
                  <li key={idx} className="flex items-start gap-2 p-2 bg-white rounded border-l-2 border-red-400">
                    <span className="text-red-500 font-bold">{idx + 1}.</span>
                    <div>
                      <span>{action.action_title || action.action || action}</span>
                      {action.owner_role && <p className="text-xs text-slate-500 mt-1">Owner: {action.owner_role}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* P1 - This Week */}
            <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
              <h4 className="font-bold text-yellow-800 mb-3 flex items-center gap-2">
                <Target className="h-4 w-4" />
                P1 - This Week
              </h4>
              <ul className="space-y-2 text-sm">
                {(actionPlan.P1_ThisWeek || []).slice(0, 5).map((action, idx) => (
                  <li key={idx} className="flex items-start gap-2 p-2 bg-white rounded border-l-2 border-yellow-400">
                    <span className="text-yellow-500 font-bold">{idx + 1}.</span>
                    <div>
                      <span>{action.action_title || action.action || action}</span>
                      {action.owner_role && <p className="text-xs text-slate-500 mt-1">Owner: {action.owner_role}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* P2 - Next Cycle */}
            <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
              <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                P2 - Next Cycle
              </h4>
              <ul className="space-y-2 text-sm">
                {(actionPlan.P2_NextCycle || []).slice(0, 5).map((action, idx) => (
                  <li key={idx} className="flex items-start gap-2 p-2 bg-white rounded border-l-2 border-green-400">
                    <span className="text-green-500 font-bold">{idx + 1}.</span>
                    <div>
                      <span>{action.action_title || action.action || action}</span>
                      {action.owner_role && <p className="text-xs text-slate-500 mt-1">Owner: {action.owner_role}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Diagnostics */}
      <Card className="bg-slate-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Diagnostics & Guardrails
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-3 bg-white rounded-lg">
              <p className="text-xs text-slate-500">Agents Succeeded</p>
              <p className="text-lg font-bold text-green-600 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                {(diagnostics.agentsSucceeded || []).length}
              </p>
              <p className="text-xs text-slate-400 mt-1">{(diagnostics.agentsSucceeded || []).join(', ').slice(0, 50)}</p>
            </div>
            <div className="p-3 bg-white rounded-lg">
              <p className="text-xs text-slate-500">Agents Failed</p>
              <p className="text-lg font-bold text-red-600 flex items-center gap-1">
                <XCircle className="h-4 w-4" />
                {(diagnostics.agentsFailed || []).length}
              </p>
            </div>
            <div className="p-3 bg-white rounded-lg">
              <p className="text-xs text-slate-500">Data Freshness</p>
              <p className="text-lg font-bold">{diagnostics.dataFreshness || 'Unknown'}</p>
            </div>
            <div className="p-3 bg-white rounded-lg">
              <p className="text-xs text-slate-500">Confidence</p>
              <p className={`text-lg font-bold ${diagnostics.confidence === 'HIGH' ? 'text-green-600' : diagnostics.confidence === 'MEDIUM' ? 'text-yellow-600' : 'text-red-600'}`}>
                {diagnostics.confidence || 'MEDIUM'}
              </p>
            </div>
          </div>
          {diagnostics.coverageNotes && diagnostics.coverageNotes.length > 0 && (
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
              <p className="text-sm font-medium text-yellow-800 mb-2">Coverage Notes:</p>
              <ul className="text-xs text-yellow-700 space-y-1">
                {diagnostics.coverageNotes.map((note, idx) => (
                  <li key={idx}>• {note}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExecutiveBrainDashboard;
